import React from "react";
import { useNavigate } from "react-router-dom";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import PaletteOutlinedIcon from "@mui/icons-material/PaletteOutlined";
import DashboardCustomizeOutlinedIcon from "@mui/icons-material/DashboardCustomizeOutlined";
import SecurityOutlinedIcon from "@mui/icons-material/SecurityOutlined";
import GavelOutlinedIcon from "@mui/icons-material/GavelOutlined";
import DoneIcon from "@mui/icons-material/Done";
import CloseIcon from "@mui/icons-material/Close";
import {
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  Tooltip,
  Button,
} from "@mui/material";

const ViewPersona: React.FC = () => {
  const navigate = useNavigate();

  // Static persona data (replace with API later)
  const persona = {
    name: "Marketing Manager",
    roleDescription: "Campaign & Content Management",
    description:
      "Responsible for managing marketing campaigns, analyzing performance metrics, and optimizing content strategy.",
    users: 6,
    icon: "Users",
    color: "Blue",
    baseTemplate: "Admin",
    risk: "Medium",
    sensitivity: "Financial / Vendor actions",
  };
  const permissions = [
    { module: "Command Center", view: true, add: false, edit: false, delete: true },
    { module: "Personas Management", view: true, add: true, edit: true, delete: false },
    { module: "Catalog & Content", view: true, add: true, edit: true, delete: false },
    { module: "Order & Logistics", view: true, add: false, edit: false, delete: true },
  ];
  const users = [
    {
      id: 1,
      name: "John Doe",
      email: "john.doe@email.com",
      role: "Manager",
      status: "Active",
      lastActive: "2h ago",
    },
    {
      id: 2,
      name: "Sarah Smith",
      email: "sarah.smith@email.com",
      role: "Editor",
      status: "Inactive",
      lastActive: "5d ago",
    },
    {
      id: 3,
      name: "David Lee",
      email: "david.lee@email.com",
      role: "Viewer",
      status: "Active",
      lastActive: "10m ago",
    },
  ];
  return (
    <div className="view_persona_page">
      <div className="main_title">
        <h2 className="m_0">View Persona</h2>
      </div>

      {/* Persona Details */}
      <div className="cards view_page">
        <div className="d_flex justify-between align-center mb_20">
          <h3 className="m_0">Persona Details</h3>
          <div className="badges_flex">
            <span className={`risk_badge ${persona.risk.toLowerCase()}`}>
              {persona.risk === "High" ? "🔴" : "🟠"} {persona.risk} Risk
            </span>
            <Chip
              label={persona.sensitivity}
              size="small"
              variant="outlined"
              color="warning"
              icon={<SecurityOutlinedIcon fontSize="small" />}
            />
          </div>
        </div>

        <div className="gap_p">
          <div className="w_33">
            <h6>Persona Name</h6>
            <p>{persona.name}</p>
          </div>

          <div className="w_33">
            <h6>Role</h6>
            <p>{persona.roleDescription}</p>
          </div>

          <div className="w_33">
            <h6>Total Users</h6>
            <p className="users_metric">
              <PeopleOutlineIcon fontSize="small" />{" "}
              <strong>{persona.users}</strong>
            </p>
          </div>

          <div className="w_100">
            <h6>Description</h6>
            <p>{persona.description}</p>
          </div>

          <div className="w_33">
            <h6>Security Level</h6>
            <p className={`security_label ${persona.risk.toLowerCase()}`}>
              <SecurityOutlinedIcon fontSize="small" /> {persona.risk} Sensitive
              Actions
            </p>
          </div>

          <div className="w_33">
            <h6>Base Template</h6>
            <p>{persona.baseTemplate}</p>
          </div>
        </div>
      </div>

      {/* Persona vs Permission Breakdown */}
      <div className="cards mt_20">
        <div className="main_title d_flex">
          <h3 className="m_0">Permissions</h3>
          <div className="right_s">
            <Button className="btn btn_primary sm" onClick={() => navigate("/manage-subadmin/roles/add")}>Add/Edit Role Module</Button>
          </div>
        </div>

        <TableContainer className="table_container">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Module</TableCell>
                <TableCell align="center">View</TableCell>
                <TableCell align="center">Add</TableCell>
                <TableCell align="center">Edit</TableCell>
                <TableCell align="center">Delete</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {permissions.map((p, i) => (
                <TableRow key={i}>
                  <TableCell>
                    <span style={{ fontSize: "13px", color: "#64748b", fontWeight: 600 }}>{p.module}</span>
                  </TableCell>
                  <TableCell align="center">
                    {p.view ? <DoneIcon color="success" fontSize="small" /> : <CloseIcon color="error" fontSize="small" />}
                  </TableCell>
                  <TableCell align="center">
                    {p.add ? <DoneIcon color="success" fontSize="small" /> : <CloseIcon color="error" fontSize="small" />}
                  </TableCell>
                  <TableCell align="center">
                    {p.edit ? <DoneIcon color="success" fontSize="small" /> : <CloseIcon color="error" fontSize="small" />}
                  </TableCell>
                  <TableCell align="center">
                    {p.delete ? <DoneIcon color="success" fontSize="small" /> : <CloseIcon color="error" fontSize="small" />}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </div>

      {/* Users Listing */}
      <div className="cards mt_20">
        <div className="main_title d_flex">
          <h3>Assigned Users</h3>
          <div className="right_s">
            <Button className="btn btn_primary sm" onClick={()=> navigate("/manage-subadmin/add")} >Add User</Button>
          </div>
        </div>

        <TableContainer className="table_container">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>User Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Last Active</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id} hover>
                  <TableCell>{user.name}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>{user.lastActive}</TableCell>
                  <TableCell>
                    <Chip
                      label={user.status}
                      size="small"
                      sx={{
                        backgroundColor:
                          user.status === "Active" ? "#dcfce7" : "#f1f5f9",
                        color: user.status === "Active" ? "#166534" : "#475569",
                        fontWeight: 600,
                      }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
    </div>
  );
};

export default ViewPersona;
